import React, { createContext, useContext, useState, useCallback } from 'react';
import { CheckCircle, XCircle, AlertTriangle, Info, X } from 'lucide-react';

const ToastContext = createContext();

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const showToast = useCallback(({ title, message, type = 'info', duration, action }) => {
    const id = Date.now() + Math.random();
    
    // Check for duplicate messages
    const isDuplicate = toasts.some(t => t.title === title && t.message === message);
    if (isDuplicate) return;

    const defaultDurations = {
      success: 3500,
      info: 3500,
      warning: 5000,
      error: 8000
    };

    const toast = {
      id,
      title,
      message,
      type,
      action,
      duration: duration || defaultDurations[type]
    };

    setToasts(prev => [...prev, toast]);

    if (toast.duration) {
      setTimeout(() => {
        removeToast(id);
      }, toast.duration);
    }
  }, [toasts]);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  // Convenience methods
  const success = useCallback((title, message, options = {}) => {
    showToast({ title, message, type: 'success', ...options });
  }, [showToast]);

  const error = useCallback((title, message, options = {}) => {
    showToast({ title, message, type: 'error', ...options });
  }, [showToast]);

  const warning = useCallback((title, message, options = {}) => {
    showToast({ title, message, type: 'warning', ...options });
  }, [showToast]);

  const info = useCallback((title, message, options = {}) => {
    showToast({ title, message, type: 'info', ...options });
  }, [showToast]);

  return (
    <ToastContext.Provider value={{ showToast, success, error, warning, info, removeToast }}>
      {children}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </ToastContext.Provider>
  );
};

const ToastContainer = ({ toasts, removeToast }) => {
  const isMobile = window.innerWidth <= 768;

  return (
    <div style={{
      position: 'fixed',
      [isMobile ? 'bottom' : 'top']: isMobile ? '20px' : '20px',
      [isMobile ? 'left' : 'right']: '20px',
      [isMobile ? 'right' : 'initial']: isMobile ? '20px' : 'initial',
      zIndex: 10000,
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
      pointerEvents: 'none',
      maxWidth: isMobile ? 'calc(100% - 40px)' : '400px'
    }}>
      {toasts.map(toast => (
        <ToastItem key={toast.id} toast={toast} onClose={() => removeToast(toast.id)} />
      ))}
    </div>
  );
};

const ToastItem = ({ toast, onClose }) => {
  const getIcon = () => {
    switch(toast.type) {
      case 'success':
        return <CheckCircle size={20} color="#10B981" />;
      case 'error':
        return <XCircle size={20} color="#EF4444" />;
      case 'warning':
        return <AlertTriangle size={20} color="#F59E0B" />;
      case 'info':
      default:
        return <Info size={20} color="#3B82F6" />;
    }
  };

  const getBorderColor = () => {
    switch(toast.type) {
      case 'success': return '#10B981';
      case 'error': return '#EF4444';
      case 'warning': return '#F59E0B';
      case 'info': return '#3B82F6';
      default: return '#E5E7EB';
    }
  };

  return (
    <div style={{
      background: '#FFFFFF',
      borderLeft: `4px solid ${getBorderColor()}`,
      borderRadius: '8px',
      padding: '14px 16px',
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      display: 'flex',
      gap: '12px',
      alignItems: 'flex-start',
      pointerEvents: 'auto',
      animation: 'slideIn 0.2s ease-out',
      minWidth: '280px',
      maxWidth: '100%'
    }}>
      <style>
        {`
          @keyframes slideIn {
            from {
              opacity: 0;
              transform: translateX(${window.innerWidth <= 768 ? '0' : '100%'}) translateY(${window.innerWidth <= 768 ? '20px' : '0'});
            }
            to {
              opacity: 1;
              transform: translateX(0) translateY(0);
            }
          }
        `}
      </style>

      {/* Icon */}
      <div style={{ flexShrink: 0, paddingTop: '2px' }}>
        {getIcon()}
      </div>

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {toast.title && (
          <div style={{
            color: '#0A0A0A',
            fontSize: '14px',
            fontWeight: '600',
            marginBottom: '2px'
          }}>
            {toast.title}
          </div>
        )}
        {toast.message && (
          <div style={{
            color: '#6B7280',
            fontSize: '13px',
            lineHeight: '1.4'
          }}>
            {toast.message}
          </div>
        )}
        {toast.action && (
          <button
            onClick={toast.action.onClick}
            style={{
              marginTop: '8px',
              padding: '4px 12px',
              background: 'transparent',
              border: '1px solid #E5E7EB',
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: '500',
              color: '#3B82F6',
              cursor: 'pointer',
              transition: 'all 0.15s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#F3F4F6';
              e.currentTarget.style.borderColor = '#3B82F6';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.borderColor = '#E5E7EB';
            }}
          >
            {toast.action.label}
          </button>
        )}
      </div>

      {/* Close button */}
      <button
        onClick={onClose}
        style={{
          background: 'transparent',
          border: 'none',
          padding: '4px',
          cursor: 'pointer',
          flexShrink: 0,
          borderRadius: '4px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'background 0.15s'
        }}
        onMouseEnter={(e) => e.currentTarget.style.background = '#F3F4F6'}
        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
      >
        <X size={16} color="#9CA3AF" />
      </button>
    </div>
  );
};

// No default export needed - using named exports (ToastProvider, useToast)
